import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { ArrowLeft, Edit, Users, FileText, Calendar, CheckSquare, Settings, MessageSquare, MoreVertical, Archive, Trash2, Network, TrendingUp, FileSignature, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { clientsAPI } from '../lib/clientsAPI';
import api from '../lib/api';
import { formatAUDate } from '../lib/dateUtils';
import { DocumentsTab } from '../components/client/DocumentsTab';
import { ClientTimeline } from '../components/client/ClientTimeline';
import { EmployeesTab } from '../components/client/EmployeesTab';
import { ComplianceTab } from '../components/client/ComplianceTab';
import { FinancialsTab } from '../components/client/FinancialsTab';
import { EngagementLettersTab } from '../components/client/EngagementLettersTab';
import { ClientAtoTab } from '../components/ato/ClientAtoTab';
import { ClearancePanel } from '../components/clearance/ClearancePanel';
import { ClientNominationTab } from '../components/nomination/ClientNominationTab';
import { NominationSummary } from '../components/nomination/NominationSummary';
import { NominationComplianceCard } from '../components/nomination/NominationComplianceCard';
import { ClientRentalTab } from '../components/rental/ClientRentalTab';
import { PortalAccessTab } from '../components/portal/PortalAccessTab';
import { IncomeTaxTab } from '../components/client/IncomeTaxTab';
import { BasTab } from '../components/client/BasTab';
import { BusinessProfileTab } from '../components/client/BusinessProfileTab';
import { ClientActions, ClientHealthBar, ClientMeta, useClientSummary } from '../components/client360/ClientHeader360';
import { ClientActivity, ClientSnapshot, ClientWorkPanels, GroupTree } from '../components/client360/ClientOverview360';
import { ScrollText } from 'lucide-react';

const entityTypeLabels = {
  company: 'Company',
  trust: 'Trust',
  smsf: 'SMSF',
  sole_trader: 'Sole Trader',
  individual: 'Individual'
};

const statusColors = {
  draft: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  active: 'bg-green-100 text-green-800 border-green-200',
  inactive: 'bg-muted text-muted-foreground border-border'
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [client, setClient] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userRole, setUserRole] = useState(undefined);
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [activeTab, setActiveTab] = useState(initialTab === 'business-profile' ? 'overview' : initialTab);
  useEffect(() => { const t = searchParams.get('tab'); if (t) setActiveTab(t === 'business-profile' ? 'overview' : t); }, [searchParams]);
  const summary = useClientSummary(id, !!userRole && userRole !== 'client');
  // Only owner/admin may delete a client (mirrors the backend role check).
  const canDelete = ['owner', 'admin'].includes(userRole);

  useEffect(() => {
    loadClient();
    loadPeople();
    loadRole();
  }, [id]);

  const loadRole = async () => {
    try {
      const response = await api.get('/api/me');
      setUserRole(response.data.active_membership?.role);
      setEnabledFeatures(response.data.enabled_features || {});
    } catch {
      // Non-fatal: if we can't determine role, the delete option stays hidden.
      setUserRole(undefined);
    }
  };

  const loadClient = async () => {
    try {
      const response = await clientsAPI.get(id);
      setClient(response.data);
    } catch (error) {
      toast.error(error?.response?.status === 403 && typeof error.response.data?.detail === 'string' ? error.response.data.detail : 'Failed to load client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const loadPeople = async () => {
    try {
      const response = await clientsAPI.listPeople(id);
      setPeople(response.data);
    } catch (error) {
      console.error('Failed to load contacts');
    }
  };

  const handleArchiveClient = async () => {
    if (!confirm('Are you sure you want to archive this client?')) return;
    
    try {
      await clientsAPI.update(id, { status: 'inactive' });
      toast.success('Client archived successfully');
      loadClient();
    } catch (error) {
      toast.error('Failed to archive client');
    }
  };

  const handleActivateClient = async () => {
    try {
      await clientsAPI.update(id, { status: 'active' });
      toast.success('Client activated successfully');
      loadClient();
    } catch (error) {
      toast.error('Failed to activate client');
    }
  };

  const handleDeleteClient = async () => {
    setDeleting(true);
    try {
      await clientsAPI.remove(id);
      toast.success('Client deleted');
      setDeleteDialogOpen(false);
      navigate('/clients'); // it no longer appears in the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenEdit = () => {
    setEditForm({
      display_name: client.display_name,
      entity_type: client.entity_type,
      abn: client.abn || '',
      acn: client.acn || '',
      gst_registered: client.gst_registered,
      bas_cycle: client.bas_cycle || 'quarterly',
      // Practice-management fields (B#9)
      payg_withholding: client.payg_withholding || false,
      paygw_cycle: client.paygw_cycle || '',
      status: client.status || 'active',
      industry: client.industry || '',
      engagement_start_date: client.engagement_start_date || '',
      accounting_software: client.accounting_software || '',
      assigned_accountant_id: client.assigned_accountant_id || '',
      xero_org_id: client.xero_org_id || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await clientsAPI.update(id, editForm);
      toast.success('Client updated successfully');
      setEditDialogOpen(false);
      loadClient();
    } catch (error) {
      toast.error('Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !client) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const isBiz = userRole && userRole !== 'client' && ['company', 'trust', 'partnership', 'sole_trader', 'smsf', 'business'].includes((client.entity_type || '').toLowerCase());

  return (
    <div className="min-w-0 space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate('/clients')}
          className="mb-4 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </Button>

        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="client-detail-name">
                {client.display_name}
              </h1>
              <Badge className={statusColors[client.status]}>
                {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
              </Badge>
              {userRole && !['client', 'read_only'].includes(userRole) && <NominationSummary clientId={client.id} compact />}
              {client.offshore_restricted?.on && <Badge variant="outline" className="border-rose-300 text-rose-700" data-testid="client-no-offshore-badge">No offshore access</Badge>}
            </div>
            {userRole && !['client', 'read_only'].includes(userRole) && <NominationSummary clientId={client.id} />}
            {summary ? <ClientMeta summary={summary} /> : <p className="text-muted-foreground">{entityTypeLabels[client.entity_type]}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2 xl:justify-end">
          {summary && <ClientActions client={client} canEdit={!['read_only', 'client'].includes(userRole)} canLetter={enabledFeatures?.accountant_letters !== false} />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="client-actions-menu">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {userRole !== 'read_only' && <DropdownMenuItem onClick={handleOpenEdit}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Client Details
              </DropdownMenuItem>}
              <DropdownMenuItem onClick={() => navigate(`/clients/${id}/structure`)}>
                <Network className="w-4 h-4 mr-2" />
                View Business Structure
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {userRole === 'read_only' ? null : client.status === 'active' ? (
                <DropdownMenuItem onClick={handleArchiveClient}>
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Client
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handleActivateClient}>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Activate Client
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-client-menu-item"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Client
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
        {summary && <div className="mt-4"><ClientHealthBar summary={summary} clientId={client.id} /></div>}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete client?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will remove <span className="font-semibold">{client?.display_name}</span> from
              your clients list. The client's data is kept (recoverable by an admin), and the matching
              contact in Xero will be <span className="font-semibold">archived</span> — not deleted.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteClient}
                disabled={deleting}
                data-testid="confirm-delete-client-btn"
              >
                {deleting ? 'Deleting…' : 'Delete Client'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex h-auto w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <FileText className="w-4 h-4 mr-2" />
            {isBiz ? 'Business Profile' : 'Overview'}
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            <MessageSquare className="w-4 h-4 mr-2" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">
            <Users className="w-4 h-4 mr-2" />
            Employees
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">
            <Users className="w-4 h-4 mr-2" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">
            <Calendar className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="financials" data-testid="tab-financials">
            <TrendingUp className="w-4 h-4 mr-2" />
            Financials
          </TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">
            <CheckSquare className="w-4 h-4 mr-2" />
            Services
          </TabsTrigger>
          <TabsTrigger value="letters" data-testid="tab-letters">
            <FileSignature className="w-4 h-4 mr-2" />
            Letters
          </TabsTrigger>
          {userRole && userRole !== 'client' && (
            <TabsTrigger value="income-tax" data-testid="tab-income-tax">
              <Landmark className="w-4 h-4 mr-2" />
              Income Tax
            </TabsTrigger>
          )}
          {userRole && userRole !== 'client' && (
            <TabsTrigger value="bas" data-testid="tab-bas">
              <Calendar className="w-4 h-4 mr-2" />
              BAS
            </TabsTrigger>
          )}
          <TabsTrigger value="rental" data-testid="tab-rental">
            <FileSignature className="w-4 h-4 mr-2" />
            Rental Properties
          </TabsTrigger>
          <TabsTrigger value="clearance" data-testid="tab-clearance">
            <FileSignature className="w-4 h-4 mr-2" />
            Clearance
          </TabsTrigger>
          {['owner', 'admin', 'accountant', 'reviewer'].includes(userRole) && (
            <TabsTrigger value="ato-nomination" data-testid="tab-ato-nomination">
              <Landmark className="w-4 h-4 mr-2" />
              ATO Agent Authorisation
            </TabsTrigger>
          )}
          {userRole && userRole !== 'client' && (
            <TabsTrigger value="portal-access" data-testid="tab-portal-access">
              <Landmark className="w-4 h-4 mr-2" />
              Portal Access
            </TabsTrigger>
          )}
          {['owner', 'admin', 'accountant', 'reviewer', 'bookkeeper', 'read_only'].includes(userRole) && (
            <TabsTrigger value="ato" data-testid="tab-ato">
              <Landmark className="w-4 h-4 mr-2" />
              ATO
            </TabsTrigger>
          )}
          {userRole && userRole !== 'client' && <TabsTrigger value="activity" data-testid="tab-activity"><ScrollText className="w-4 h-4 mr-2" />Activity</TabsTrigger>}
        </TabsList>

        {userRole && userRole !== 'client' && <TabsContent value="activity">{summary ? <ClientActivity items={summary.activity} />
          : summary === false ? <p className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground" data-testid="client-activity-error">Activity couldn't be loaded. Refresh the page to try again.</p>
          : <div className="space-y-3 rounded-lg border border-border bg-card p-5" data-testid="client-activity-loading">{[0, 1, 2, 3].map((i) => <div key={i} className="h-4 animate-pulse rounded bg-muted" style={{ width: `${80 - i * 12}%` }} />)}</div>}</TabsContent>}

        {/* Overview Tab */}
        <TabsContent value="overview" className="min-w-0 space-y-6">
          {summary && <div className="grid min-w-0 gap-6 xl:grid-cols-3 [&>*]:min-w-0" data-testid="client-overview-360">
            <div className="space-y-6"><ClientSnapshot summary={summary} /><GroupTree tree={summary.group_tree} /></div>
            <div className="xl:col-span-2"><ClientWorkPanels summary={summary} clientId={client.id} /></div>
          </div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Business Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Business Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {client.abn && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ABN:</span>
                    <span className="font-medium font-mono">{client.abn}</span>
                  </div>
                )}
                {client.acn && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ACN:</span>
                    <span className="font-medium font-mono">{client.acn}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GST Registered:</span>
                  <span className="font-medium">{client.gst_registered ? 'Yes' : 'No'}</span>
                </div>
                {client.gst_registered && client.bas_cycle && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">BAS Cycle:</span>
                    <span className="font-medium capitalize">{client.bas_cycle}</span>
                  </div>
                )}
                <div className="flex justify-between" data-testid="client-paygw">
                  <span className="text-muted-foreground">PAYG Withholding:</span>
                  <span className="font-medium">{client.payg_withholding ? `Yes${client.paygw_cycle ? ` – ${client.paygw_cycle === 'monthly' ? 'monthly (IAS)' : 'quarterly (on BAS)'}` : ' – cycle not set'}` : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Entity Type:</span>
                  <span className="font-medium">{entityTypeLabels[client.entity_type]}</span>
                </div>
              </CardContent>
            </Card>

            {/* Key Dates */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Key Dates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="font-medium">{formatAUDate(client.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Updated:</span>
                  <span className="font-medium">{formatAUDate(client.updated_at)}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Onboarding Progress */}
          {client.status === 'draft' && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardHeader>
                <CardTitle className="text-lg text-yellow-900" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Onboarding In Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-yellow-800 mb-4">
                  This client's onboarding is not yet complete. Complete the remaining steps to activate the client.
                </p>
                <Button className="bg-primary hover:bg-primary/90">
                  Continue Onboarding
                </Button>
              </CardContent>
            </Card>
          )}
          {isBiz && userRole !== 'read_only' && <BusinessProfileTab clientId={client.id} />}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <DocumentsTab clientId={client.id} />
        </TabsContent>

        {/* Communications Tab */}
        <TabsContent value="timeline">
          <ClientTimeline clientId={client.id} canEdit={userRole && userRole !== 'read_only' && userRole !== 'client'} />
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees">
          <EmployeesTab clientId={client.id} />
        </TabsContent>

        <TabsContent value="income-tax">
          <IncomeTaxTab clientId={client.id} />
        </TabsContent>
        <TabsContent value="business-profile">
          <BusinessProfileTab clientId={client.id} />
        </TabsContent>
        <TabsContent value="bas">
          <BasTab clientId={client.id} />
        </TabsContent>

        <TabsContent value="rental">
          <ClientRentalTab clientId={client.id} />
        </TabsContent>

        <TabsContent value="clearance">
          <ClearancePanel clientId={client.id} />
        </TabsContent>

        {/* Compliance Tab (B#13) */}
        <TabsContent value="compliance">
          {userRole && userRole !== 'client' && <NominationComplianceCard clientId={client.id} />}
          <ComplianceTab clientId={client.id} />
        </TabsContent>

        {/* Financials Tab (Xero import) */}
        <TabsContent value="financials">
          <FinancialsTab clientId={client.id} />
        </TabsContent>

        {/* Engagement Letters Tab */}
        <TabsContent value="letters">
          <EngagementLettersTab clientId={client.id} role={userRole} onClientChanged={loadClient} />
        </TabsContent>

        <TabsContent value="ato-nomination">
          {['owner', 'admin', 'accountant', 'reviewer'].includes(userRole) && <ClientNominationTab clientId={client.id} />}
        </TabsContent>

        <TabsContent value="portal-access">
          {userRole && userRole !== 'client' && <PortalAccessTab clientId={client.id} />}
        </TabsContent>

        <TabsContent value="ato">
          {userRole && userRole !== 'client' && <ClientAtoTab clientId={client.id} />}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Contacts</CardTitle>
              <Button size="sm" variant="outline">
                <Users className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </CardHeader>
            <CardContent>
              {people.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p>No contacts added yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {people.map((person) => (
                    <div key={person.id} className="flex items-start justify-between p-4 border rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-foreground">{person.name}</h4>
                          {person.is_primary && (
                            <Badge className="bg-primary text-white">Primary</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground capitalize">{person.role.replace('_', ' ')}</p>
                        {person.email && <p className="text-sm text-muted-foreground">{person.email}</p>}
                        {person.phone && <p className="text-sm text-muted-foreground">{person.phone}</p>}
                      </div>
                      <Button variant="ghost" size="sm">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Active Services</CardTitle>
            </CardHeader>
            <CardContent>
              {!client.services || client.services.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p>No services selected</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {client.services.map((service) => (
                    <div key={service} className="p-4 border rounded-lg flex items-center gap-3">
                      <CheckSquare className="w-5 h-5 text-primary" />
                      <span className="font-medium capitalize">{service.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Client Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Client Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Business Name</Label>
              <Input
                id="edit_name"
                value={editForm.display_name || ''}
                onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_entity">Entity Type</Label>
              <Select 
                value={editForm.entity_type || 'company'} 
                onValueChange={(value) => setEditForm({...editForm, entity_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="trust">Trust</SelectItem>
                  <SelectItem value="smsf">SMSF</SelectItem>
                  <SelectItem value="sole_trader">Sole Trader</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_abn">ABN</Label>
                <Input
                  id="edit_abn"
                  value={editForm.abn || ''}
                  onChange={(e) => setEditForm({...editForm, abn: e.target.value})}
                  maxLength={11}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_acn">ACN</Label>
                <Input
                  id="edit_acn"
                  value={editForm.acn || ''}
                  onChange={(e) => setEditForm({...editForm, acn: e.target.value})}
                  maxLength={9}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit_gst"
                checked={editForm.gst_registered || false}
                onCheckedChange={(checked) => setEditForm({...editForm, gst_registered: checked})}
              />
              <Label htmlFor="edit_gst" className="cursor-pointer">GST Registered</Label>
            </div>

            {editForm.gst_registered && (
              <div className="space-y-2">
                <Label htmlFor="edit_bas">BAS Cycle</Label>
                <Select
                  value={editForm.bas_cycle || 'quarterly'}
                  onValueChange={(value) => setEditForm({...editForm, bas_cycle: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit_payg"
                checked={editForm.payg_withholding || false}
                onCheckedChange={(checked) => setEditForm({...editForm, payg_withholding: checked})}
              />
              <Label htmlFor="edit_payg" className="cursor-pointer">PAYG Withholding Registered</Label>
            </div>

            {editForm.payg_withholding && (
              <div className="space-y-2">
                <Label>PAYG withholding reporting</Label>
                <Select value={editForm.paygw_cycle || ''} onValueChange={(value) => setEditForm({...editForm, paygw_cycle: value})}>
                  <SelectTrigger data-testid="edit-paygw-cycle"><SelectValue placeholder="Choose cycle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly – separate IAS</SelectItem>
                    <SelectItem value="quarterly">Quarterly – on the BAS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Practice-management fields (B#9 / spec Module B) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_status">Status</Label>
                <Select
                  value={editForm.status || 'active'}
                  onValueChange={(value) => setEditForm({...editForm, status: value})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_industry">Industry</Label>
                <Input
                  id="edit_industry"
                  value={editForm.industry || ''}
                  onChange={(e) => setEditForm({...editForm, industry: e.target.value})}
                  placeholder="e.g. Hospitality"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_engagement">Engagement Start Date</Label>
                <Input
                  id="edit_engagement"
                  type="date"
                  value={editForm.engagement_start_date || ''}
                  onChange={(e) => setEditForm({...editForm, engagement_start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_software">Accounting Software</Label>
                <Input
                  id="edit_software"
                  value={editForm.accounting_software || ''}
                  onChange={(e) => setEditForm({...editForm, accounting_software: e.target.value})}
                  placeholder="e.g. Xero, MYOB"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_accountant">Assigned Accountant</Label>
                <Input
                  id="edit_accountant"
                  value={editForm.assigned_accountant_id || ''}
                  onChange={(e) => setEditForm({...editForm, assigned_accountant_id: e.target.value})}
                  placeholder="Staff user ID"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_xero">Xero Organisation ID</Label>
                <Input
                  id="edit_xero"
                  value={editForm.xero_org_id || ''}
                  onChange={(e) => setEditForm({...editForm, xero_org_id: e.target.value})}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEdit} 
                disabled={saving}
                className="bg-primary hover:bg-primary/90"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
